/**
 * Capacite de bus **`klavox.credentials`** v1 — KLAVOX est le DESTINATAIRE.
 *
 * Contrat : `bus/klavox-credentials.v1.md` (pose aussi dans `navkrypt/bus/`).
 * Un pair de la galaxie (NAVKRYPT, a la reprise du navigateur precedent)
 * remet des identifiants au coffre. Le choix qui fait le contrat :
 *
 *   - NAVKRYPT ne recoit JAMAIS la vaultKey (le coffre est zero-knowledge) ;
 *   - et les identifiants ne traversent JAMAIS en clair la frontiere de
 *     processus.
 *
 * D'ou le modele de CXP (Credential Exchange Protocol, FIDO) : KLAVOX, coffre
 * ouvert et geste de l'utilisateur fait, emet une cle publique EPHEMERE ; le
 * pair chiffre la charge CXF v1.0 vers elle en HPKE (RFC 9180) ; KLAVOX
 * l'ouvre en memoire, la verse par `parseCxfHeader` puis la rechiffre sous la
 * vaultKey, et oublie la cle privee.
 *
 * Ce module est la PORTE d'entree : il lit les messages du pair comme des
 * donnees non fiables et refuse toute enveloppe qui pourrait porter un secret
 * en clair — champ inconnu (`password`, `entries`, `cxf`...), chiffre qui se
 * relit comme du texte ou une archive. Il ne dechiffre rien, n'ouvre aucune
 * connexion et ne persiste rien. Un refus ne cite jamais la charge.
 */

export const CREDENTIALS_TRANSFER_CAPABILITY = 'klavox.credentials';
export const CREDENTIALS_TRANSFER_VERSION = 1;

/**
 * Suite HPKE imposee (RFC 9180, mode base) : DHKEM(X25519, HKDF-SHA256) /
 * HKDF-SHA256 / AES-256-GCM — identifiants 0x0020 / 0x0001 / 0x0002. Une seule
 * suite : pas de negociation, donc pas de retrogradation possible.
 */
export const CREDENTIALS_TRANSFER_SUITE = 'X25519-HKDF-SHA256/HKDF-SHA256/AES-256-GCM';

/** `enc` HPKE pour DHKEM(X25519) : la cle publique ephemere de l'emetteur, 32 octets. */
export const HPKE_ENC_LENGTH = 32;
/** Tag AES-256-GCM : un chiffre plus court ne peut rien contenir. */
export const HPKE_TAG_LENGTH = 16;
/** Plafond d'une charge : un coffre de plusieurs milliers d'entrees tient tres en dessous. */
export const CREDENTIALS_TRANSFER_MAX_BYTES = 16 * 1024 * 1024;

/** Identifiant de transfert : 16 octets aleatoires en base64url (22 caracteres). */
const TRANSFER_ID = /^[A-Za-z0-9_-]{22}$/;
const SOURCE_ID = /^[a-z0-9][a-z0-9._-]{0,63}$/;
const BASE64URL = /^[A-Za-z0-9_-]+$/;
const NOM_MAX = 80;

export type CredentialsTransferRefus =
  | 'illisible'
  | 'incompatible'
  | 'version'
  | 'op'
  | 'champ-inconnu'
  | 'source'
  | 'transfert'
  | 'enc'
  | 'ciphertext'
  | 'trop-gros'
  | 'en-clair';

export interface CredentialsTransferOffer {
  readonly op: 'offer';
  readonly source: { readonly id: string; readonly name: string };
  /** Nombre annonce d'identifiants, affiche dans la fenetre de consentement. */
  readonly count: number;
}

export interface CredentialsTransferDelivery {
  readonly op: 'deliver';
  readonly transferId: string;
  readonly enc: Uint8Array;
  readonly ciphertext: Uint8Array;
}

export type CredentialsTransferMessage = CredentialsTransferOffer | CredentialsTransferDelivery;

export type CredentialsTransferParse =
  | { readonly ok: true; readonly message: CredentialsTransferMessage }
  | { readonly ok: false; readonly refus: CredentialsTransferRefus };

/** Champs admis, au nom pres. Tout autre champ est refuse : c'est la que glisserait un secret. */
const CHAMPS: Readonly<Record<'offer' | 'deliver', readonly string[]>> = {
  offer: ['capability', 'version', 'op', 'source', 'count'],
  deliver: ['capability', 'version', 'op', 'transferId', 'enc', 'ciphertext'],
};
const CHAMPS_SOURCE = ['id', 'name'];

function refus(raison: CredentialsTransferRefus): CredentialsTransferParse {
  return { ok: false, refus: raison };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function seulement(objet: Record<string, unknown>, admis: readonly string[]): boolean {
  return Object.keys(objet).every((cle) => admis.includes(cle));
}

function base64UrlToBytes(value: string): Uint8Array | null {
  if (!BASE64URL.test(value) || value.length % 4 === 1) return null;
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binaire = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return octets;
}

/** Signatures d'archives : une charge compressee reste une charge EN CLAIR. */
const SIGNATURES_ARCHIVE: readonly (readonly number[])[] = [
  [0x1f, 0x8b], // gzip
  [0x50, 0x4b, 0x03, 0x04], // zip
  [0x28, 0xb5, 0x2f, 0xfd], // zstd
];

/**
 * Un chiffre AEAD est indiscernable de l'aleatoire : environ 37 % d'octets
 * imprimables. Au-dela de 90 %, c'est du texte — un CSV, un JSON CXF, un
 * mot de passe — qu'un pair a range dans `ciphertext` sans le chiffrer.
 */
export function looksLikePlaintext(octets: Uint8Array): boolean {
  for (const signature of SIGNATURES_ARCHIVE) {
    if (signature.every((octet, i) => octets[i] === octet)) return true;
  }
  let imprimables = 0;
  for (const octet of octets) {
    if ((octet >= 0x20 && octet <= 0x7e) || octet === 0x09 || octet === 0x0a || octet === 0x0d) imprimables += 1;
  }
  return imprimables >= octets.length * 0.9;
}

/** Nom affiche a l'utilisateur : sans controle, invisible ni inversion bidi, tronque. */
function nomAffichable(value: string): string {
  return value
    .replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁠-⁩﻿]/g, '')
    .trim()
    .slice(0, NOM_MAX);
}

function parseOffer(message: Record<string, unknown>): CredentialsTransferParse {
  const source = message['source'];
  if (!isRecord(source) || !seulement(source, CHAMPS_SOURCE)) return refus('source');
  const id = source['id'];
  const name = source['name'];
  if (typeof id !== 'string' || !SOURCE_ID.test(id) || typeof name !== 'string') return refus('source');
  const nom = nomAffichable(name);
  if (nom === '') return refus('source');
  const count = message['count'];
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 1) return refus('op');
  return { ok: true, message: { op: 'offer', source: { id, name: nom }, count } };
}

function parseDelivery(message: Record<string, unknown>): CredentialsTransferParse {
  const transferId = message['transferId'];
  if (typeof transferId !== 'string' || !TRANSFER_ID.test(transferId)) return refus('transfert');

  const enc = typeof message['enc'] === 'string' ? base64UrlToBytes(message['enc']) : null;
  if (enc === null || enc.length !== HPKE_ENC_LENGTH) return refus('enc');

  const brut = message['ciphertext'];
  if (typeof brut !== 'string') return refus('ciphertext');
  // Borne AVANT de decoder : une chaine geante ne doit rien couter.
  if (brut.length > Math.ceil((CREDENTIALS_TRANSFER_MAX_BYTES * 4) / 3)) return refus('trop-gros');
  const ciphertext = base64UrlToBytes(brut);
  if (ciphertext === null || ciphertext.length <= HPKE_TAG_LENGTH) return refus('ciphertext');
  if (looksLikePlaintext(ciphertext)) return refus('en-clair');

  return { ok: true, message: { op: 'deliver', transferId, enc, ciphertext } };
}

/**
 * Point d'entree UNIQUE : tout message du pair passe par ici avant que le
 * coffre n'en fasse quoi que ce soit. Accepte l'objet deja desserialise (le
 * canal `postMessage` le livre ainsi) ou sa forme JSON.
 */
export function parseCredentialsTransfer(raw: unknown): CredentialsTransferParse {
  let message: unknown = raw;
  if (typeof raw === 'string') {
    try {
      message = JSON.parse(raw);
    } catch {
      return refus('illisible');
    }
  }
  if (!isRecord(message)) return refus('illisible');
  if (message['capability'] !== CREDENTIALS_TRANSFER_CAPABILITY) return refus('incompatible');
  if (message['version'] !== CREDENTIALS_TRANSFER_VERSION) return refus('version');
  const op = message['op'];
  if (op !== 'offer' && op !== 'deliver') return refus('op');
  if (!seulement(message, CHAMPS[op])) return refus('champ-inconnu');
  return op === 'offer' ? parseOffer(message) : parseDelivery(message);
}
